import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

export default function AgentBoard() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState('');

  const [agentWeights, setAgentWeights] = useState(() => {
    try {
      const saved = localStorage.getItem('agentWeights');
      return saved ? JSON.parse(saved) : { timeline: 0.40, pre_approval: 0.30, budget_size: 0.20, motivation: 0.10 };
    } catch { return { timeline: 0.40, pre_approval: 0.30, budget_size: 0.20, motivation: 0.10 }; }
  });

  const updateWeight = (key, value) => {
    const updated = { ...agentWeights, [key]: parseFloat(value) };
    setAgentWeights(updated);
    localStorage.setItem('agentWeights', JSON.stringify(updated));
  };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [propsRes, buyersRes] = await Promise.all([
        authFetch(`${API}/api/properties?limit=200`),
        authFetch(`${API}/api/buyers`),
      ]);

      let propsList = [];
      let buyersList = [];
      if (propsRes.ok) propsList = await propsRes.json();
      if (buyersRes.ok) buyersList = await buyersRes.json();

      const allScores = {};
      for (const buyer of buyersList) {
        const boardRes = await authFetch(`${API}/api/board/buyer/${buyer.id}`);
        if (boardRes.ok) {
          const boardData = await boardRes.json();
          if (Array.isArray(boardData)) {
            boardData.forEach(entry => {
              const propId = entry.property_id;
              if (!allScores[propId]) allScores[propId] = [];
              allScores[propId].push({
                buyer_id: buyer.id,
                buyer: buyer,
                fit_score: entry.fit_score || 0,
                tier: entry.tier,
                // Capture dimension scores so the budget-size weight can use
                // the Financials dimension as its factor input.
                dimension_scores: entry.dimension_scores || {},
              });
            });
          }
        }
      }

      const enriched = propsList.map(prop => {
        const matched = allScores[prop.id] || [];
        const scored = matched.filter(b => b.fit_score > 0);
        const avgScore = scored.length > 0 ? scored.reduce((sum, b) => sum + b.fit_score, 0) / scored.length : 0;
        const topEntry = scored.length > 0 ? scored.sort((a, b) => b.fit_score - a.fit_score)[0] : null;
        return {
          ...prop,
          buyers_matched: scored.length,
          matched_buyers: scored,
          avg_score: avgScore,
          top_score: topEntry ? topEntry.fit_score : 0,
          top_tier: topEntry ? topEntry.tier : null,
        };
      });

      setProperties(enriched);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Factor lookup tables — all return 0.0 to 1.0 so the sliders combine
  // cleanly into a single readiness score in [0, sum(weights)].
  // Timeline urgency: how soon the client needs to be in space.
  const TIMELINE_FACTOR = {
    '30_days': 1.0, '60_days': 0.7, '90_days': 0.5, '6_months': 0.3,
    'exploring': 0.1, 'ready_now': 1.0,
  };
  // Financing posture: how funded are they really.
  const FINANCE_FACTOR = {
    'approved': 1.0, 'pre_approved': 1.0,
    'in_process': 0.6, 'in_progress': 0.6,
    'not_started': 0.2, 'unknown': 0.1,
  };
  // Motivation: how serious is the search.
  const URGENCY_FACTOR = {
    'must_move': 1.0, 'highly_motivated': 1.0,
    'want_to_move': 0.7, 'motivated': 0.4,
    'casually_looking': 0.2, 'casual': 0.2,
  };

  // Per-match readiness in [0, 1]. Blends the 4 slider weights with the
  // factor scores. Each factor is 0-1; weights are 0-1; we divide by total
  // weight so the result is also bounded 0-1 regardless of slider values.
  const calcReadiness = (match) => {
    const tF = TIMELINE_FACTOR[match.buyer?.timeline] ?? 0.1;
    const fF = FINANCE_FACTOR[match.buyer?.pre_approval_status] ?? 0.1;
    // Budget-size factor uses the Financials dimension of the fit score —
    // measures how well the property's price matches what the client can pay.
    const fin = match.dimension_scores?.['Financials'];
    const bF = fin != null ? Math.max(0, Math.min(1, fin / 9)) : 0.5;
    const uF = URGENCY_FACTOR[match.buyer?.motivation] ?? 0.2;

    const w = agentWeights;
    const totalWeight = (w.timeline || 0) + (w.pre_approval || 0) + (w.budget_size || 0) + (w.motivation || 0);
    if (totalWeight <= 0) return 0;

    const weighted = (w.timeline * tF) + (w.pre_approval * fF) + (w.budget_size * bF) + (w.motivation * uF);
    return weighted / totalWeight; // 0..1
  };

  // Transaction likelihood per (property, client) — readiness × normalized fit.
  // Result is 0-1; multiply by 100 for display.
  const calcPairLikelihood = (match) => {
    const fitNorm = match.fit_score ? Math.max(0, Math.min(1, match.fit_score / 9)) : 0;
    return calcReadiness(match) * fitNorm;
  };

  // Property sort key — sum of pair likelihoods so properties with more ready
  // clients rise (additive, like the old behavior, just on a 0-1 base).
  const calcPropertyRank = (prop) => {
    if (!prop.matched_buyers || prop.matched_buyers.length === 0) return 0;
    return prop.matched_buyers.reduce((sum, m) => sum + calcPairLikelihood(m), 0);
  };

  // Display value — the BEST single-client likelihood for this property,
  // shown as a 0-100% badge. Sum drives the sort, max drives the display.
  const calcPropertyDisplayLikelihood = (prop) => {
    if (!prop.matched_buyers || prop.matched_buyers.length === 0) return 0;
    return Math.max(...prop.matched_buyers.map(calcPairLikelihood));
  };

  const listingTypeBadge = (lt) => {
    if (lt === 'for_sale')           return { label: 'FOR SALE',     cls: 'bg-green-900/50 text-green-300 border-green-600/40' };
    if (lt === 'for_sale_and_lease') return { label: 'SALE & LEASE', cls: 'bg-orange-900/50 text-orange-300 border-orange-600/40' };
    if (lt === 'for_lease')          return { label: 'FOR LEASE',    cls: 'bg-blue-900/50 text-blue-300 border-blue-600/40' };
    return null;
  };

  const getTierBadge = (tier) => {
    if (!tier) return null;
    const colors = {
      'ELITE FIT': 'bg-emerald-900/70 text-emerald-300 border-emerald-500/30',
      'STRONG FIT': 'bg-green-900/50 text-green-300 border-green-500/20',
      'GOOD FIT': 'bg-yellow-900/40 text-yellow-300 border-yellow-500/20',
      'MARGINAL FIT': 'bg-orange-900/40 text-orange-300 border-orange-500/20',
      'POOR FIT': 'bg-red-900/40 text-red-300 border-red-500/20',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[tier] || 'bg-gray-800 text-gray-400'}`}>
        {tier}
      </span>
    );
  };

  const getDemandBar = (count, max) => {
    const pct = max > 0 ? (count / max) * 100 : 0;
    let color = 'bg-gray-700';
    if (count >= 3) color = 'bg-emerald-500';
    else if (count >= 2) color = 'bg-green-500';
    else if (count >= 1) color = 'bg-yellow-500';
    return (
      <div className="flex items-center gap-2">
        <div className="w-20 bg-gray-800 rounded-full h-2">
          <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }}></div>
        </div>
        <span className="text-white text-sm font-medium">{count}</span>
      </div>
    );
  };

  const formatPrice = (p) => p ? `$${Number(p).toLocaleString()}` : '—';

  // Color-band the transaction likelihood — gives the broker an at-a-glance
  // read without doing math on the number.
  const likelihoodColor = (pct) => {
    if (pct >= 70) return { text: 'text-[#00E676]', bar: 'bg-[#00E676]' };
    if (pct >= 50) return { text: 'text-[#00D4FF]', bar: 'bg-[#00D4FF]' };
    if (pct >= 30) return { text: 'text-yellow-300', bar: 'bg-yellow-400' };
    if (pct >= 15) return { text: 'text-orange-300', bar: 'bg-orange-400' };
    return { text: 'text-red-300', bar: 'bg-red-500' };
  };

  // Filter to only properties with buyers, then sort by rank score
  const withBuyers = properties.filter(p => p.buyers_matched > 0);
  const maxBuyers = Math.max(...withBuyers.map(p => p.buyers_matched), 1);

  const sorted = [...withBuyers].sort((a, b) => calcPropertyRank(b) - calcPropertyRank(a));

  const filtered = filterTier ? sorted.filter(p => p.top_tier === filterTier) : sorted;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Building master board...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-6xl mx-auto px-3 sm:px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white">Master Board</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">Properties ranked by likelihood to transact based on your client priorities</p>
          </div>
          <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)}
            className="px-3 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
            <option value="">All Tiers</option>
            <option value="ELITE FIT">Elite Fit</option>
            <option value="STRONG FIT">Strong Fit</option>
            <option value="GOOD FIT">Good Fit</option>
            <option value="MARGINAL FIT">Marginal Fit</option>
          </select>
        </div>

        {/* Agent Priority Weights */}
        <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/20 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 md:flex-wrap">
            <span className="text-gray-400 text-sm font-medium">What matters most today?</span>
            {[
              { key: 'timeline', label: 'Timeline' },
              { key: 'pre_approval', label: 'Financial Status' },
              { key: 'budget_size', label: 'Budget Size' },
              { key: 'motivation', label: 'Urgency' },
            ].map(w => (
              <div key={w.key} className="flex items-center gap-2 w-full md:w-auto">
                <span className="text-gray-300 text-xs w-24 md:w-auto">{w.label}</span>
                <input type="range" min="0" max="1" step="0.05" value={agentWeights[w.key]}
                  onChange={(e) => updateWeight(w.key, e.target.value)}
                  className="flex-1 md:flex-none md:w-20 accent-[#00D4FF] h-1.5" />
                <span className="text-[#00D4FF] text-xs font-medium w-8">{(agentWeights[w.key] * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-xs mt-2">Adjust to reprioritize which properties rise to the top based on your clients' readiness.</p>
        </div>

        {/* Property List - Mobile Cards */}
        <div className="md:hidden space-y-3">
          {filtered.map((prop, i) => (
            <div key={prop.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-gray-500 text-xs">#{i + 1}</span>
                    {getTierBadge(prop.top_tier)}
                    {(() => { const lt = listingTypeBadge(prop.listing_type); return lt && <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${lt.cls}`}>{lt.label}</span>; })()}
                  </div>
                  <Link to={`/properties/${prop.id}`} className="text-white font-medium text-sm hover:text-[#00D4FF] transition-colors block">
                    {prop.address}
                  </Link>
                  <div className="text-gray-500 text-xs mt-0.5">
                    {prop.city}, {prop.state}
                  </div>
                </div>
                <div className="text-right whitespace-nowrap">
                  {prop.price != null ? (
                    <>
                      <div className="text-[#00E676] font-medium text-sm">{formatPrice(prop.price)}</div>
                      {prop.lease_rate_sqft != null && (
                        <div className="text-[#00E676] text-xs">${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr</div>
                      )}
                    </>
                  ) : prop.lease_rate_sqft != null ? (
                    <div className="text-[#00E676] font-medium text-sm">${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr</div>
                  ) : (
                    <div className="text-gray-500 font-medium text-sm">—</div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-[#546E7A]/20">
                <div>
                  <div className="text-gray-500 text-xs mb-0.5">Top Score</div>
                  <div className="text-white font-bold text-base">
                    {prop.top_score > 0 ? Math.round((prop.top_score / 9) * 100) + '%' : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs mb-0.5">Transact %</div>
                  {(() => {
                    const pct = Math.round(calcPropertyDisplayLikelihood(prop) * 100);
                    const c = likelihoodColor(pct);
                    return (
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-base tabular-nums ${c.text}`}>{pct}%</span>
                        <div className="flex-1 max-w-[80px] bg-[#0A0E27] rounded-full h-1.5 overflow-hidden">
                          <div className={`h-1.5 rounded-full ${c.bar}`} style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500 text-xs mb-1">Demand</div>
                  {getDemandBar(prop.buyers_matched, maxBuyers)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Property List - Desktop Table */}
        <div className="hidden md:block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#546E7A]/30">
                  <th className="text-center px-3 py-3 text-sm font-medium text-[#B0BEC5] w-10">#</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Property</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-[#B0BEC5]">Price</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Client Matches</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Top Score</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]" title="Readiness × Fit, weighted by sliders">Transact %</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Top Tier</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((prop, i) => (
                  <tr key={prop.id} className={`border-b border-[#546E7A]/10 hover:bg-[#243B55]/30 transition-colors ${i % 2 === 1 ? 'bg-[#0A0E27]/20' : ''}`}>
                    <td className="px-3 py-3 text-center text-gray-500 text-sm font-medium">{i + 1}</td>
                    <td className="px-4 py-3">
                      <Link to={`/properties/${prop.id}`} className="text-white font-medium text-sm hover:text-[#00D4FF] transition-colors inline-flex items-center gap-2">
                        {prop.address}
                        {(() => { const lt = listingTypeBadge(prop.listing_type); return lt && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${lt.cls}`}>{lt.label}</span>; })()}
                      </Link>
                      <div className="text-gray-500 text-xs">
                        {prop.city}, {prop.state} · {prop.sqft?.toLocaleString()} sqft
                        {prop.mls_number && ` · Listing: ${prop.mls_number}`}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {prop.price != null ? (
                        <>
                          <div className="text-[#00E676] font-medium text-sm">{formatPrice(prop.price)}</div>
                          {prop.lease_rate_sqft != null && (
                            <div className="text-gray-400 text-xs">${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr</div>
                          )}
                        </>
                      ) : prop.lease_rate_sqft != null ? (
                        <div className="text-[#00E676] font-medium text-sm">${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr</div>
                      ) : (
                        <span className="text-gray-500 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">{getDemandBar(prop.buyers_matched, maxBuyers)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-white font-bold text-sm">{prop.top_score > 0 ? Math.round((prop.top_score / 9) * 100) + '%' : '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const pct = Math.round(calcPropertyDisplayLikelihood(prop) * 100);
                        const c = likelihoodColor(pct);
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <span className={`font-bold text-sm tabular-nums ${c.text}`}>{pct}%</span>
                            <div className="w-16 bg-[#0A0E27] rounded-full h-1.5 overflow-hidden">
                              <div className={`h-1.5 rounded-full ${c.bar}`} style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center">{getTierBadge(prop.top_tier)}</td>
                    <td className="px-4 py-3 text-center">
                      <Link to={`/properties/${prop.id}?tab=buyers`}
                        className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-xs font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors whitespace-nowrap">
                        See Clients
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
          <span>Demand bar = number of clients matched</span>
          <span>·</span>
          <span>Transact % = client readiness × property fit, weighted by your sliders</span>
          <span>·</span>
          <span>Adjust sliders to re-rank in real time.</span>
        </div>
      </div>
    </div>
  );
}
